import { useState } from 'react';
import { ArrowLeftRight, Check, Circle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';

/**
 * ScenarioSelector - Professional baseline/comparison scenario selector
 * 
 * Features:
 * - Two dropdowns: Baseline (left) and Comparison (right)
 * - Swap button to reverse baseline ↔ comparison
 * - Clear visual distinction between baseline and comparison
 * - Badges for "Live/Active" scenario
 * - Disabled state when same scenario selected on both sides
 */
export default function ScenarioSelector({
  scenarios = [],
  baselineId,
  comparisonId,
  onBaselineChange,
  onComparisonChange,
  onSwap,
  loading = false,
  disabled = false,
}) {
  const isValidSelection = baselineId && comparisonId && baselineId !== comparisonId;

  const getScenarioLabel = (scenario) => {
    return scenario?.name || 'Select Scenario';
  };

  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const value = Math.abs(cents) / 100;
    return value >= 1000000 
      ? `$${(value / 1000000).toFixed(1)}M`
      : value >= 1000
      ? `$${(value / 1000).toFixed(1)}K`
      : `$${value.toFixed(0)}`;
  };

  const formatNumber = (num) => {
    return num?.toLocaleString('en-US') || '0';
  };

  return (
    <Card className="border-gray-200 shadow-sm">
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row items-center gap-6">
          {/* Baseline Scenario */}
          <div className="flex-1 w-full">
            <div className="mb-2">
              <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Baseline</span>
            </div>
            <Select
              value={baselineId || ''}
              onValueChange={onBaselineChange}
              disabled={loading || disabled}
            >
              <SelectTrigger className="w-full h-12 border-2 border-gray-200 hover:border-gray-300 bg-white">
                <SelectValue placeholder="Select baseline scenario" />
              </SelectTrigger>
              <SelectContent>
                {scenarios.map((scenario) => (
                  <SelectItem 
                    key={scenario.id} 
                    value={scenario.id}
                    disabled={scenario.id === comparisonId}
                  >
                    <div className="flex items-center gap-2">
                      <span>{scenario.name}</span>
                      {scenario.is_active && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          LIVE
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Scenario Info */}
            {baselineId && (
              <div className="mt-2 px-3 py-2 bg-gray-50 rounded-md">
                {(() => {
                  const scenario = scenarios.find(s => s.id === baselineId);
                  return scenario ? (
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>Volume: <strong>{formatNumber(scenario.total_volume)}</strong></span>
                      <span className="text-gray-300">|</span>
                      <span>Cost: <strong>{formatCurrency(scenario.total_tariff_cost_cents)}</strong></span>
                    </div>
                  ) : null;
                })()}
              </div>
            )}
          </div>

          {/* Swap Button */}
          <div className="flex items-center justify-center lg:mt-6">
            <Button
              variant="outline"
              size="icon"
              onClick={onSwap}
              disabled={!isValidSelection || loading || disabled}
              className="rounded-full h-10 w-10 border-2 hover:bg-gray-100"
            >
              <ArrowLeftRight className="h-4 w-4 text-gray-600" />
            </Button>
          </div>

          {/* Comparison Scenario */}
          <div className="flex-1 w-full">
            <div className="mb-2">
              <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Comparison</span>
            </div>
            <Select
              value={comparisonId || ''}
              onValueChange={onComparisonChange}
              disabled={loading || disabled}
            >
              <SelectTrigger className="w-full h-12 border-2 border-blue-200 hover:border-blue-300 bg-blue-50/30">
                <SelectValue placeholder="Select comparison scenario" />
              </SelectTrigger>
              <SelectContent>
                {scenarios.map((scenario) => (
                  <SelectItem 
                    key={scenario.id} 
                    value={scenario.id}
                    disabled={scenario.id === baselineId}
                  >
                    <div className="flex items-center gap-2">
                      <span>{scenario.name}</span>
                      {scenario.is_active && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          LIVE
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Scenario Info */}
            {comparisonId && (
              <div className="mt-2 px-3 py-2 bg-blue-50 rounded-md">
                {(() => {
                  const scenario = scenarios.find(s => s.id === comparisonId);
                  return scenario ? (
                    <div className="flex items-center gap-4 text-sm text-blue-700">
                      <span>Volume: <strong>{formatNumber(scenario.total_volume)}</strong></span>
                      <span className="text-blue-300">|</span>
                      <span>Cost: <strong>{formatCurrency(scenario.total_tariff_cost_cents)}</strong></span>
                    </div>
                  ) : null;
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Validation Message */}
        {baselineId === comparisonId && baselineId && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-sm text-amber-700">Please select different scenarios for baseline and comparison.</p>
          </div>
        )}

        {/* Selection Summary */}
        {isValidSelection && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
            <Check className="h-4 w-4 text-green-600" />
            <span>
              Comparing <strong>{scenarios.find(s => s.id === baselineId)?.name}</strong> (baseline) 
              vs <strong>{scenarios.find(s => s.id === comparisonId)?.name}</strong> (comparison)
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
